import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { match } from 'ts-pattern';

import { useCommunityProfilesCount } from '~/api/community';
import { useUser } from '~/api/user';
import { Button, LinkButton, Spinner, Text } from '~/components';
import { availablePrimaryActivities } from '~/constants/constants';
import { AnchorTargets, SearchParamKeys } from '~/constants/url';
import { routes } from '~/providers/RouterProvider/router.routes';
import { includes } from '~/utils/arrays';
import { qs } from '~/utils/searchParams';

import { NetworkInfoCard } from './NetworkInfoCard';
import { cardButtonStyle, cardsStyle, largeButtonStyle } from './NetworkInfoCards.css';
import { useCommunityProfileFilters } from './NetworkOverview.nuqs';

export const NetworkInfoCards = () => {
  const filters = useCommunityProfileFilters();

  const { data: user } = useUser();
  const regions = user.communityProfile.regions;
  const activityIds = user.activities.map((activity) => activity.id).filter((id) => includes(availablePrimaryActivities, id));

  const { data: regionProfilesCount, status: regionProfilesStatus } = useCommunityProfilesCount(
    { activityIds: [], name: '', openToWork: false, regions },
    { enabled: regions.length > 0 },
  );
  const { data: activityProfilesCount, status: activityProfilesStatus } = useCommunityProfilesCount(
    { activityIds, name: '', openToWork: false, regions: [] },
    { enabled: activityIds.length > 0 },
  );

  const { pathname } = useLocation();
  const { t } = useTranslation('community');

  const showRegionResults = () => filters.regions.onChange(regions);
  const showActivityResults = () => filters.activityIds.onChange(activityIds);

  return (
    <div className={cardsStyle}>
      {regions.length > 0 && (
        <NetworkInfoCard
          action={
            <Button className={cardButtonStyle} emphasis="tertiary" icon="ArrowRight" iconPlacement="right" onClick={showRegionResults}>
              {t('infoCards.region.action')}
            </Button>
          }
          description={match(regionProfilesStatus)
            .with('pending', () => <Spinner size="inline" />)
            .with('error', () => <Text>{t('infoCards.error')}</Text>)
            .with('success', () => <Text>{t('infoCards.region.description', { count: regionProfilesCount })}</Text>)
            .exhaustive()}
          illustration="PersonComputer3"
          illustrationStrokeWidth={1.0}
          title={t('infoCards.region.title')}
        />
      )}

      {activityIds.length > 0 && (
        <NetworkInfoCard
          action={
            <Button className={cardButtonStyle} emphasis="tertiary" icon="ArrowRight" iconPlacement="right" onClick={showActivityResults}>
              {t('infoCards.activity.action')}
            </Button>
          }
          description={match(activityProfilesStatus)
            .with('pending', () => <Spinner size="inline" />)
            .with('error', () => <Text>{t('infoCards.error')}</Text>)
            .with('success', () => <Text>{t('infoCards.activity.description', { count: activityProfilesCount })}</Text>)
            .exhaustive()}
          illustration="PersonCamera"
          illustrationStrokeWidth={1.0}
          title={t('infoCards.activity.title')}
        />
      )}

      <NetworkInfoCard
        action={
          <LinkButton
            className={largeButtonStyle}
            emphasis="primary"
            icon="Edit"
            to={{
              pathname: routes.settingsProfile,
              hash: AnchorTargets.SETTINGS_PROFILE_COMMUNITY,
              search: qs({ [SearchParamKeys.REDIRECT_PATH]: pathname }),
            }}
          >
            {t('infoCards.profile.action')}
          </LinkButton>
        }
        description={<Text>{t('infoCards.profile.description')}</Text>}
        title={t('infoCards.profile.title')}
        variant="primary-brand"
      />
    </div>
  );
};
